import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import {
	fetchLatestSchedule,
	fetchCoverageRequests,
} from '../../../services/teacherViewData';

// Local calendar day as "YYYY-MM-DD" — never toISOString (UTC-shift trap).
function todayKey() {
	const now = new Date();
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// Format a "YYYY-MM-DD" key as a local date (no timezone shift from UTC parsing).
function formatDate(key) {
	const [y, m, d] = (key || '').split('-').map(Number);
	if (!y || !m || !d) return key || '';
	const dt = new Date(y, m - 1, d);
	return dt.toLocaleDateString(undefined, {
		weekday: 'short',
		month: 'short',
		day: 'numeric',
	});
}

const prettyLocation = (loc) =>
	loc
		? loc.charAt(0).toUpperCase() + loc.slice(1).toLowerCase()
		: 'Location TBD';

// Teacher dashboard — read-only schedule (gated viewSchedule). Shows the
// released rotation, each date from the run that owns it (fetchLatestSchedule). Default scope is the teacher's own (filtered by
// her teachers-doc id, resolved from users/{uid}.teacherId); a toggle reveals the
// full studio. Own-vs-all is a view toggle, NOT a permission.
function TeacherSchedule({ teacherId }) {
	const [data, setData] = useState(null); // { run, assignments, teachersById }
	const [status, setStatus] = useState('loading'); // loading | ready | error
	const [scope, setScope] = useState('own'); // own | all
	const [requests, setRequests] = useState([]);
	const [coverageDay, setCoverageDay] = useState(null); // key of the day whose reason form is open
	const [coverageReason, setCoverageReason] = useState('');
	const [coverageBusy, setCoverageBusy] = useState(false);
	const [coverageError, setCoverageError] = useState(null);

	const reloadRequests = useCallback(async (runs) => {
		try {
			setRequests(await fetchCoverageRequests((runs || []).map((r) => r.id)));
		} catch (err) {
			console.error('Failed to load coverage requests:', err);
		}
	}, []);

	useEffect(() => {
		let alive = true;
		fetchLatestSchedule()
			.then(async (res) => {
				if (!alive) return;
				setData(res);
				setStatus('ready');
				if (res.run) await reloadRequests(res.runs);
			})
			.catch((err) => {
				console.error('Failed to load schedule:', err);
				if (alive) setStatus('error');
			});
		return () => {
			alive = false;
		};
	}, [reloadRequests]);

	const rows = useMemo(() => {
		if (!data) return [];
		if (scope === 'own') {
			if (!teacherId) return [];
			return data.assignments.filter((a) => a.teacherId === teacherId);
		}
		return data.assignments;
	}, [data, scope, teacherId]);

	// My upcoming day-locations, deduped — the grain a coverage request names.
	const myDays = useMemo(() => {
		if (!data || !teacherId) return [];
		const today = todayKey();
		const seen = new Map();
		for (const a of data.assignments) {
			if (a.teacherId !== teacherId || !a.date || a.date < today) continue;
			const key = `${a.date}::${a.location}`;
			if (!seen.has(key)) seen.set(key, { key, date: a.date, location: a.location });
		}
		return [...seen.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
	}, [data, teacherId]);

	const requestByKey = useMemo(() => {
		const m = new Map();
		for (const r of requests) {
			const existing = m.get(r.dayLocationKey);
			// An open request outranks a decided one for display.
			if (!existing || r.status === 'open') m.set(r.dayLocationKey, r);
		}
		return m;
	}, [requests]);

	const openRequests = useMemo(
		() => requests.filter((r) => r.status === 'open'),
		[requests]
	);

	const submitCoverage = async (day) => {
		if (coverageBusy) return;
		setCoverageBusy(true);
		setCoverageError(null);
		try {
			await httpsCallable(functions, 'requestCoverage')({
				date: day.date,
				location: day.location,
				reason: coverageReason.trim() || null,
			});
			setCoverageDay(null);
			setCoverageReason('');
			if (data?.run) await reloadRequests(data.runs);
		} catch (err) {
			console.error('Coverage request failed:', err);
			setCoverageError(err.message || 'Could not send the request.');
		} finally {
			setCoverageBusy(false);
		}
	};

	const cancelCoverage = async (requestId) => {
		if (coverageBusy) return;
		setCoverageBusy(true);
		setCoverageError(null);
		try {
			await httpsCallable(functions, 'cancelCoverageRequest')({ requestId });
			if (data?.run) await reloadRequests(data.runs);
		} catch (err) {
			console.error('Coverage cancel failed:', err);
			setCoverageError(err.message || 'Could not cancel the request.');
		} finally {
			setCoverageBusy(false);
		}
	};

	if (status === 'loading') {
		return <div className='td-state'>Loading schedule…</div>;
	}
	if (status === 'error') {
		return (
			<div className='td-state'>
				Couldn’t load the schedule. Try again in a moment.
			</div>
		);
	}
	if (!data.run) {
		return (
			<div className='td-state'>
				No schedule has been generated yet.
			</div>
		);
	}

	const teachersById = data.teachersById || {};

	return (
		<div className='td-schedule'>
			<div className='td-schedule__bar'>
				<div className='td-seg' role='group' aria-label='Schedule scope'>
					<button
						type='button'
						className={`td-seg__btn${
							scope === 'own' ? ' is-active' : ''
						}`}
						onClick={() => setScope('own')}>
						My schedule
					</button>
					<button
						type='button'
						className={`td-seg__btn${
							scope === 'all' ? ' is-active' : ''
						}`}
						onClick={() => setScope('all')}>
						Full studio
					</button>
				</div>
				<span className='td-schedule__count'>
					{rows.length} assignment{rows.length === 1 ? '' : 's'}
				</span>
			</div>

			{openRequests.length > 0 && (
				<div className='td-coverage__strip'>
					<strong>Open coverage requests:</strong>
					{openRequests.map((r) => (
						<span key={r.id} className='td-coverage__stripitem'>
							{(teachersById[r.teacherId] || 'A teacher') + ' — ' + formatDate(r.date) + ' at ' + prettyLocation(r.location) + (r.reason ? ` (${r.reason})` : '')}
						</span>
					))}
				</div>
			)}

			{scope === 'own' && teacherId && myDays.length > 0 && (
				<div className='td-coverage' data-guide='coverage'>
					<div className='td-coverage__head'>Need a day covered?</div>
					<ul className='td-coverage__list'>
						{myDays.map((day) => {
							const req = requestByKey.get(day.key);
							const mineOpen = req && req.status === 'open';
							return (
								<li key={day.key} className='td-coverage__row'>
									<span className='td-coverage__when'>
										{formatDate(day.date)} · {prettyLocation(day.location)}
									</span>
									{!req && coverageDay !== day.key && (
										<button
											type='button'
											className='td-coverage__btn'
											onClick={() => {
												setCoverageDay(day.key);
												setCoverageReason('');
												setCoverageError(null);
											}}>
											Request coverage
										</button>
									)}
									{!req && coverageDay === day.key && (
										<span className='td-coverage__form'>
											<input
												type='text'
												placeholder='Reason (optional)'
												maxLength={300}
												value={coverageReason}
												onChange={(ev) => setCoverageReason(ev.target.value)}
											/>
											<button
												type='button'
												className='td-coverage__btn'
												disabled={coverageBusy}
												onClick={() => submitCoverage(day)}>
												{coverageBusy ? 'Sending…' : 'Send request'}
											</button>
											<button
												type='button'
												className='td-coverage__btn td-coverage__btn--quiet'
												onClick={() => setCoverageDay(null)}>
												Never mind
											</button>
										</span>
									)}
									{mineOpen && (
										<span className='td-coverage__status'>
											Requested — waiting on Anthony or Grace
											<button
												type='button'
												className='td-coverage__btn td-coverage__btn--quiet'
												disabled={coverageBusy}
												onClick={() => cancelCoverage(req.id)}>
												Cancel
											</button>
										</span>
									)}
									{req && req.status === 'approved' && (
										<span className='td-coverage__status td-coverage__status--ok'>
											Covered by {teachersById[req.coveredByTeacherId] || 'another teacher'}
										</span>
									)}
									{req && req.status === 'declined' && (
										<span className='td-coverage__status td-coverage__status--no'>
											Declined{req.decidedByName ? ` by ${req.decidedByName}` : ''}
											{req.decisionNote ? ` — ${req.decisionNote}` : ''}
										</span>
									)}
								</li>
							);
						})}
					</ul>
					{coverageError && (
						<div className='td-coverage__error'>{coverageError}</div>
					)}
				</div>
			)}

			{scope === 'own' && !teacherId ? (
				<div className='td-state'>
					Your teaching profile isn’t linked yet — ask an admin to
					finish your setup, or switch to “Full studio”.
				</div>
			) : rows.length === 0 ? (
				<div className='td-state'>
					{scope === 'own'
						? 'Nothing scheduled for you in the latest run.'
						: 'No assignments in the latest run.'}
				</div>
			) : (
				<ul className='td-schedule__list'>
					{rows.map((a) => (
						<li key={a.id} className='td-assign'>
							<div className='td-assign__date'>
								{formatDate(a.date)}
							</div>
							<div className='td-assign__body'>
								<span className='td-chip'>
									<i className='fa-sharp fa-solid fa-location-dot' />
									{prettyLocation(a.location)}
								</span>
								{scope === 'all' && (
									<span className='td-assign__teacher'>
										{teachersById[a.teacherId] ||
											'Unassigned'}
									</span>
								)}
							</div>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export default TeacherSchedule;
